import React, { PureComponent } from 'react';
import { NavLink as Link } from 'react-router-dom';
import ImageLinks from 'utils/ImageLinks';
import HighlightButton from 'components/Button/HighlightButton';
import request from 'utils/request';
import LoadingSpinner from 'components/LoadingSpinner';
import InputWithIcon from 'components/Input/InputWithIcon';

import 'features/internal-users/user/Register/Register.scss';
import { withTranslation } from 'react-i18next';
import { TRANSLATIONS } from 'types/enums';
import TextWithIcon from 'components/Text/TextWithIcon';
import withParams from 'utils/hoc/withParams';
import PasswordSetSuccess from '../Register/PasswordSetSuccess';
import './RecoverPassword.scss';

type State = {
  password?: string;
  passwordConfirm?: string;
  submitting?: boolean;
  errors?: any;
  email?: string;
  isLoadingUserInfo?: boolean;
  passwordChanged?: boolean;
  target?: any;
  passwordCriteria?: boolean;
  isSuperUser?: boolean;
};
class RecoverPassword extends PureComponent<any, State> {
  missingCriteria = [
    { icon: ImageLinks.info, text: 'CAPITAL_LETTER' },
    { icon: ImageLinks.info, text: 'LOWER_CASE' },
    { icon: ImageLinks.info, text: 'NUMBER' },
    { icon: ImageLinks.info, text: 'SPECIAL_CHARACTER' },
    { icon: ImageLinks.info, text: 'PASSWORD_MIN_CHAR' },
  ];

  constructor(props: any) {
    super(props);
    this.state = {
      password: '',
      passwordConfirm: '',
      submitting: false,
      errors: {},
      email: '',
      isLoadingUserInfo: true,
      passwordChanged: false,
    };
    this.submit = this.submit.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.enterSubmit = this.enterSubmit.bind(this);
    this.validatePassword = this.validatePassword.bind(this);
  }

  async componentDidMount() {
    try {
      const d = await request.get(`/api/v2/users/token/${this.props.params.token}`);
      this.setState({
        isLoadingUserInfo: false,
        email: ((d.user && d.user.email) || ''),
        isSuperUser: ((d.user && d.user.hasUserManagementAccess)),
      });
    } catch (error) {
      if ((error as any)
        .response?.status === 401) return this.setState({ isLoadingUserInfo: false });
      if ((typeof (error as any).response.data) !== 'object') {
        this.setState({ errors: { message: 'Error de red' }, isLoadingUserInfo: false });
      } else {
        this.setState({ errors: (error as any).response.data, isLoadingUserInfo: false });
      }
    }
  }

  validatePasswordLen(pass: string) {
    if (this.state.isSuperUser) {
      return pass.length >= 15;
    } else {
      return pass.length >= 10;
    }
  }

  validatePassword(pass: string) {
    const specialCharacters = '@$!%*?&-/()=¿^_´¨,.¢#|';
    const specialCharactersRegex = new RegExp(`[${specialCharacters}]`);
    const isMinLength = this.validatePasswordLen(pass);

    this.missingCriteria.forEach((criteria, index) => {
      let isCriteriaMet = false;
      let passCriteria = false;
      switch (index) {
        case 0: // Capital letter
          isCriteriaMet = /[A-Z]/.test(pass);
          break;
        case 1: // Lower case
          isCriteriaMet = /[a-z]/.test(pass);
          break;
        case 2: // Number
          isCriteriaMet = /[0-9]/.test(pass);
          break;
        case 3: // Special character
          isCriteriaMet = specialCharactersRegex.test(pass);
          break;
        case 4: // Min char by type of user
          isCriteriaMet = isMinLength;
          break;
        default:
          break;
      }
      if (/[A-Z]/.test(pass) && /[a-z]/.test(pass) && /[0-9]/.test(pass) && specialCharactersRegex.test(pass) && isMinLength) {
        passCriteria = true;
      }

      criteria.icon = isCriteriaMet ? ImageLinks.checked : ImageLinks.info;

      this.setState({ passwordCriteria: passCriteria });
    });
  }

  async submit() {
    const { t } = this.props;
    const { passwordCriteria } = this.state;
    this.setState({ errors: {} });
    if (this.state.password!.length < 7) {
      this.setState({ errors: { password: { msg: t(TRANSLATIONS.PASSWORD_TOO_SHORT) } } });
      return null;
    }
    if (!passwordCriteria) {
      this.setState(
        { errors: { message: t(TRANSLATIONS.VERIFY_PASSWORD_CRITERIA),
          password: '' },
        submitting: false },
      );
      return null;
    }
    if (this.state.password !== this.state.passwordConfirm) {
      this.setState({ errors: { passwordConfirm: { msg: t(TRANSLATIONS.PASSWORD_NO_MATCH) } } });
      return null;
    }
    let compared;
    try {
      compared = await request.post('/api/v1/users/getUserPasswordCompare', {
        token: this.props.params.token,
        password: this.state.password,
      });
    } catch (error) {
      this.setState({ errors: { message: 'Error de red. Por favor vuélvelo a intentar.' }, submitting: false });
    }
    if (compared?.currentPasswordIsEqual) {
      this.setState({ errors: { password:
        { msg: t(TRANSLATIONS.DO_NOT_USE_YOUR_OLD_PASSWORD) } } });
      return null;
    }
    if (compared?.userHasRepeatedPassword) {
      this.setState({
        errors: { password: { msg: t(TRANSLATIONS.DO_NOT_USE_ONE_OF_YOUR_OLD_PASSWORDS) } },
      });
      return null;
    }
    this.setState({ submitting: true, errors: {} });
    try {
      await request.post('/api/v2/users/register', {
        accessToken: this.props.params.token,
        password: this.state.password,
      });
      this.setState({ passwordChanged: true });
    } catch (error) {
      if ((typeof (error as any).response.data) !== 'object') {
        this.setState({ errors: { message: 'Error de red' }, submitting: false });
      } else {
        this.setState({ errors: (error as any).response.data, submitting: false });
      }
    }
  }

  handleInputChange(e: State) {
    this.setState({ [e.target.name]: e.target.value });
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    e.target.name === 'password' && this.validatePassword(e.target.value);
  }

  enterSubmit(e: React.KeyboardEvent<HTMLFormElement>) {
    if (e.keyCode === 13 && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      this.submit();
    }
  }

  render() {
    const { errors } = this.state;
    const { t } = this.props;
    // 500 error responses are not formatted within input field keys
    // but rather contain a single message key
    let nonValidationError = '';
    if (!errors.email && !errors.password && errors.message) {
      nonValidationError = errors.message;
    }
    if (this.state.passwordChanged) {
      return (
        <PasswordSetSuccess register={false} />
      );
    }
    return (
      <div className="register-bg">
        <div className="register">
          <div className="container">
            <div className="row">
              <div className="col-md-6 offset-md-3 card main-card-container">
                <p className="header-text">
                  {t(TRANSLATIONS.PASSWORD_RECOVERY)}<br /><span className="font-bold">Safety 360</span>
                </p>
                {nonValidationError ? (
                  <h3 className="error text-center spacing-top">
                    {nonValidationError}
                  </h3>
                ) : null}
                {!this.state.isLoadingUserInfo
                  ? this.state.email === ''
                    ? (
                      <div className="invalid-token">
                        <h3>{nonValidationError || t(TRANSLATIONS.THIS_URL_HAS_EXPIRED)}</h3>
                        <Link to="/" className="link go-to-home"><p>{t(TRANSLATIONS.GO_TO_LOGIN)}</p></Link>
                      </div>
                    )
                    : (
                      <>
                        <p className="welcome-text">
                          {t(TRANSLATIONS.UPDATE_YOUR_PASSWORD_TO_ACCESS)}
                        </p>
                        <InputWithIcon
                          icon={ImageLinks.userIcon}
                          errorIcon={ImageLinks.userWarningIcon}
                          type="text"
                          placeholder={t(TRANSLATIONS.EMAIL)}
                          onChange={() => ''}
                          value={this.state.email}
                          error={errors.email && errors.email.msg}
                          errorHeight
                          name="email"
                        />
                        <form onSubmit={this.submit} onKeyDown={this.enterSubmit}>
                          <InputWithIcon
                            icon={ImageLinks.lockIcon}
                            errorIcon={ImageLinks.lockWarningIcon}
                            type="password"
                            placeholder={t(TRANSLATIONS.PASSWORD)}
                            onChange={this.handleInputChange}
                            name="password"
                            errorHeight
                            error={errors.password && errors.password.msg}
                            clickable
                            iconRight={ImageLinks.eyex2}
                            rightImageShowPassword
                          />
                          <p className="text-margin">
                            {t(TRANSLATIONS.PASSWORD_MUST)}:
                          </p>
                          {this.missingCriteria.map((item, index) => (
                            <>
                              <div className={`centered-container${index === this.missingCriteria.length - 1 ? ' marginBottomToLast' : ''}`}>
                                <TextWithIcon
                                  icon={item.icon}
                                  text={`${t(TRANSLATIONS.CONTAIN)} ${t(item.text)}`}
                                />
                              </div>
                            </>
                          ))}
                          <InputWithIcon
                            icon={ImageLinks.lockIcon}
                            errorIcon={ImageLinks.lockWarningIcon}
                            type="password"
                            placeholder={t(TRANSLATIONS.CONFIRM_PASSWORD)}
                            onChange={this.handleInputChange}
                            name="passwordConfirm"
                            errorHeight
                            error={errors.passwordConfirm && errors.passwordConfirm.msg}
                            clickable
                            iconRight={ImageLinks.eyex2}
                            rightImageShowPassword
                          />
                          <p className="remember-password-text">
                            {t(TRANSLATIONS.I_REMEMBER_MY_PASSWORD)}
                            <Link
                              to="/login"
                              style={{ textDecoration: 'underline' }}
                            >
                              {t(TRANSLATIONS.CLICK_HERE)}
                            </Link>
                          </p>
                        </form>
                        <HighlightButton
                          text={t(TRANSLATIONS.UPDATE).toUpperCase()}
                          className="main-action"
                          onClick={this.submit}
                        />
                        {this.state.submitting && <LoadingSpinner className="register-spinner" />}
                      </>
                    )
                  : <LoadingSpinner height={397} />}
              </div>
            </div>
            <div className="row">
              <div className="footer">
                <p className="footer-text"> powered by ARVOLUTION </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default withTranslation()(withParams(RecoverPassword));
